import { DEFAULT_LANG } from '../LanguageContext';
import {
  ChildQuestionTemplate,
  FormQuestionItem,
  FormTemplate,
  isPageBreak,
  Option,
  PAGE_BREAK_QUESTION_TYPE,
  Question,
  StringsEntry,
  StringsFile,
  StructureFile,
} from '../types';
import { getPlainText } from './InputType';
import { logClientError } from './logClientError';

export const QUESTION_STRING_FIELDS = [
  'text',
  'question_plain_text',
  'description',
  'scale_min_text',
  'scale_max_text',
  'measurement_unit',
] as const;

export const CHILD_TEMPLATE_STRING_FIELDS = [
  'text_before',
  'text_after',
  'description',
  'measurement_unit',
] as const;

export const STRINGS_FETCH_ATTEMPTS = 2;

function deepCloneFormTemplate(structure: FormTemplate): FormTemplate {
  return JSON.parse(JSON.stringify(structure)) as FormTemplate;
}

function overlayStringFields(
  target: Record<string, unknown>,
  source: Record<string, unknown>,
  fields: readonly string[],
): void {
  for (const field of fields) {
    if (field in source) {
      target[field] = source[field];
    }
  }
}

function overlayOptionTexts(
  options: Option[],
  optionStrings: StringsEntry['options'] | undefined,
): void {
  if (!optionStrings) {
    return;
  }
  for (const option of options) {
    const optionEntry = optionStrings[option.guid];
    if (optionEntry && 'text' in optionEntry) {
      option.text = optionEntry.text;
    }
  }
}

function overlayChildTemplates(
  childTemplates: ChildQuestionTemplate[] | undefined,
  childStrings: StringsEntry['child_templates'] | undefined,
): void {
  if (!childTemplates || !childStrings) {
    return;
  }
  for (const template of childTemplates) {
    const templateEntry = childStrings[template.id];
    if (!templateEntry) {
      continue;
    }
    overlayStringFields(
      template as unknown as Record<string, unknown>,
      templateEntry as unknown as Record<string, unknown>,
      CHILD_TEMPLATE_STRING_FIELDS,
    );
    overlayOptionTexts(template.options ?? [], templateEntry.options);
  }
}

export function overlayQuestionStrings(
  question: FormQuestionItem,
  questionStrings: StringsEntry,
): void {
  overlayStringFields(
    question as unknown as Record<string, unknown>,
    questionStrings as unknown as Record<string, unknown>,
    QUESTION_STRING_FIELDS,
  );
  overlayOptionTexts(question.options, questionStrings.options);
  overlayChildTemplates(question.child_question_templates, questionStrings.child_templates);
}

function blankStringFields(target: Record<string, unknown>, fields: readonly string[]): void {
  for (const field of fields) {
    if (field in target) {
      target[field] = '';
    }
  }
}

/**
 * One language's view of a question: Hebrew structure with that language's text. Every text
 * slot is blanked first so an untranslated slot shows empty instead of Hebrew.
 */
export function questionInLang(
  hebrewQuestion: Question,
  entry: StringsEntry | undefined,
): Question {
  const question = JSON.parse(JSON.stringify(hebrewQuestion)) as Question;
  blankStringFields(question as unknown as Record<string, unknown>, QUESTION_STRING_FIELDS);
  question.options = question.options ?? [];
  question.options.forEach((option) => (option.text = ''));
  for (const template of question.child_question_templates ?? []) {
    blankStringFields(template as unknown as Record<string, unknown>, CHILD_TEMPLATE_STRING_FIELDS);
    template.options?.forEach((option) => (option.text = ''));
  }
  if (entry) {
    overlayQuestionStrings(question as FormQuestionItem, entry);
  }
  return question;
}

function nonEmptyStringFields(
  source: Record<string, unknown>,
  fields: readonly string[],
): Record<string, string> {
  const result: Record<string, string> = {};
  for (const field of fields) {
    const value = source[field];
    if (typeof value === 'string' && value !== '') {
      result[field] = value;
    }
  }
  return result;
}

function optionTexts(options: Option[] | undefined): Record<string, { text: string }> | undefined {
  const texts: Record<string, { text: string }> = {};
  for (const option of options ?? []) {
    if (String(option.text ?? '') !== '') {
      texts[option.guid] = { text: String(option.text) };
    }
  }
  return Object.keys(texts).length > 0 ? texts : undefined;
}

/** Inverse of questionInLang. Built from the current structure, so dropped guids leave no orphans. */
export function entryFromQuestion(langQuestion: Question): StringsEntry {
  const entry = nonEmptyStringFields(
    langQuestion as unknown as Record<string, unknown>,
    QUESTION_STRING_FIELDS.filter((field) => field !== 'question_plain_text'),
  ) as StringsEntry;
  if (entry.text) {
    entry.question_plain_text = getPlainText(entry.text);
  }
  const options = optionTexts(langQuestion.options);
  if (options) {
    entry.options = options;
  }
  const childTemplates: NonNullable<StringsEntry['child_templates']> = {};
  for (const template of langQuestion.child_question_templates ?? []) {
    const templateEntry = nonEmptyStringFields(
      template as unknown as Record<string, unknown>,
      CHILD_TEMPLATE_STRING_FIELDS,
    ) as NonNullable<StringsEntry['child_templates']>[string];
    const templateOptions = optionTexts(template.options);
    if (templateOptions) {
      templateEntry.options = templateOptions;
    }
    if (Object.keys(templateEntry).length > 0) {
      childTemplates[template.id] = templateEntry;
    }
  }
  if (Object.keys(childTemplates).length > 0) {
    entry.child_templates = childTemplates;
  }
  return entry;
}

export type MissingSlot = {
  slot: string;
  optionGuid?: string;
  childTemplateId?: string;
};

function hasVisibleText(value: unknown): boolean {
  if (typeof value !== 'string' && typeof value !== 'number') {
    return false;
  }
  return getPlainText(String(value)).trim() !== '';
}

/**
 * Slots where Hebrew has visible text and this language does not. Hidden questions and page
 * breaks are skipped, matching the server completeness check.
 */
export function missingSlots(hebrewQuestion: Question, langQuestion: Question): MissingSlot[] {
  if (
    hebrewQuestion.show_question === false ||
    hebrewQuestion.question_type === PAGE_BREAK_QUESTION_TYPE
  ) {
    return [];
  }
  const missing: MissingSlot[] = [];
  const check = (hebrewValue: unknown, langValue: unknown, slot: MissingSlot) => {
    if (hasVisibleText(hebrewValue) && !hasVisibleText(langValue)) {
      missing.push(slot);
    }
  };
  const checkOptions = (
    hebrewOptions: Option[] | undefined,
    langOptions: Option[] | undefined,
    childTemplateId?: string,
  ) => {
    for (const option of hebrewOptions ?? []) {
      const langOption = langOptions?.find((o) => o.guid === option.guid);
      check(option.text, langOption?.text, {
        slot: 'option',
        optionGuid: option.guid,
        childTemplateId,
      });
    }
  };

  const hebrew = hebrewQuestion as unknown as Record<string, unknown>;
  const lang = langQuestion as unknown as Record<string, unknown>;
  for (const field of QUESTION_STRING_FIELDS) {
    if (field !== 'question_plain_text') {
      check(hebrew[field], lang[field], { slot: field });
    }
  }
  checkOptions(hebrewQuestion.options, langQuestion.options);
  for (const template of hebrewQuestion.child_question_templates ?? []) {
    const langTemplate = langQuestion.child_question_templates?.find((t) => t.id === template.id);
    for (const field of CHILD_TEMPLATE_STRING_FIELDS) {
      check(
        (template as unknown as Record<string, unknown>)[field],
        (langTemplate as unknown as Record<string, unknown> | undefined)?.[field],
        { slot: field, childTemplateId: template.id },
      );
    }
    checkOptions(template.options, langTemplate?.options, template.id);
  }
  return missing;
}

/**
 * Strings for an auto-generated child in one language, built like createChildQuestions does
 * for Hebrew. Child options get fresh guids, so they map to template options by index.
 */
export function childEntry(langParent: Question, child: Question): StringsEntry {
  const template = langParent.child_question_templates?.find(
    (t) => t.id === child.auto_generated_from_template,
  );
  if (!template) {
    return {};
  }
  const optionGuid = child.appeared_conditions?.find(
    (cond) => cond.connected_question_guid === langParent.guid,
  )?.values?.[0];
  const optionText = langParent.options.find((option) => option.guid === optionGuid)?.text ?? '';
  // A bare option label is not a translated question; leave it blank so the child shows red.
  const hasTemplateText = hasVisibleText(template.text_before) || hasVisibleText(template.text_after);
  return entryFromQuestion({
    ...questionInLang(child, undefined),
    text: hasTemplateText
      ? `${template.text_before ?? ''}${String(optionText)}${template.text_after ?? ''}`
      : '',
    description: template.description?.trim() ?? '',
    measurement_unit: template.measurement_unit,
    options: (child.options ?? []).map((option, index) => ({
      ...option,
      text: template.options?.[index]?.text ?? '',
    })),
  });
}

/**
 * One language's strings for a parent's children after the parent is saved. A child that
 * already has text keeps it: translators write whole sentences the template cannot rebuild.
 */
export function childStringsForLang(
  prevLangStrings: StringsFile,
  langParent: Question,
  children: Question[],
): StringsFile {
  const result: StringsFile = {};
  for (const child of children) {
    const existing = prevLangStrings[child.guid];
    result[child.guid] = hasVisibleText(existing?.text) ? existing : childEntry(langParent, child);
  }
  return result;
}

export type LoadedSplitTemplate = {
  structure: StructureFile;
  stringsByLang: Record<string, StringsFile>;
  displayLang: string;
  usedLegacyFallback: boolean;
  /** Languages whose strings fetch errored (not 404); their real files may exist in S3. */
  failedLangs: string[];
};

export type SplitTemplateFetchDeps = {
  getStructure: (formId: string) => Promise<StructureFile | null>;
  getStrings: (formId: string, lang: string) => Promise<StringsFile | null>;
  getLegacyMono: (formId: string) => Promise<FormTemplate>;
};

async function fetchStringsOrNull(
  formId: string,
  lang: string,
  getStrings: SplitTemplateFetchDeps['getStrings'],
  failedLangs: string[],
): Promise<StringsFile | null> {
  for (let attempt = 1; ; attempt++) {
    try {
      return await getStrings(formId, lang);
    } catch (error) {
      if (attempt < STRINGS_FETCH_ATTEMPTS) continue;
      logClientError('loadSplitTemplateArtifacts.stringsFetchFailed', { formId, lang, error });
      failedLangs.push(lang);
      return null;
    }
  }
}

/**
 * stringsByLang holds only files that were actually fetched; a missing language is
 * absent rather than filled with Hebrew, so callers can fall back to DEFAULT_LANG themselves.
 * A 404 is just absent; a fetch error is also listed in failedLangs.
 */
export async function loadSplitTemplateArtifacts(
  formId: string,
  declaredLangs: string[],
  deps: SplitTemplateFetchDeps,
): Promise<LoadedSplitTemplate | FormTemplate> {
  const langs = Array.from(new Set([DEFAULT_LANG, ...declaredLangs]));

  const structure = await deps.getStructure(formId);
  if (structure === null) {
    // TODO(PSIFAS-12308): restore logClientError once every form is migrated; until then a
    // missing structure is the expected path for unmigrated forms, not a failure.
    if (process.env.NODE_ENV === 'development') {
      console.debug('[loadSplitTemplateArtifacts.legacyMonoFallback]', { formId });
    }
    return deps.getLegacyMono(formId);
  }

  const failedLangs: string[] = [];
  const fetched = await Promise.all(
    langs.map((lang) => fetchStringsOrNull(formId, lang, deps.getStrings, failedLangs)),
  );
  const stringsByLang: Record<string, StringsFile> = {};
  langs.forEach((lang, index) => {
    const strings = fetched[index];
    if (strings !== null) {
      stringsByLang[lang] = strings;
    }
  });

  const displayLang =
    stringsByLang[DEFAULT_LANG] !== undefined
      ? DEFAULT_LANG
      : (langs.find((lang) => stringsByLang[lang] !== undefined) ?? DEFAULT_LANG);

  const displayStrings = stringsByLang[displayLang];
  if (!displayStrings) {
    logClientError('loadSplitTemplateArtifacts.noStringsFallback', { formId });
    return deps.getLegacyMono(formId);
  }

  return {
    structure,
    stringsByLang,
    displayLang,
    usedLegacyFallback: false,
    failedLangs,
  };
}

/** Return merged FormTemplate for editor display (Hebrew/default lang until PSIFAS-12302). */
export function mergedFormTemplateFromLoadResult(
  result: LoadedSplitTemplate | FormTemplate,
): FormTemplate {
  if ('questions' in result) {
    return result;
  }
  return mergeStructureAndStrings(result.structure, result.stringsByLang[result.displayLang]);
}

/**
 * Overlay guid-keyed strings onto a structure copy and return the merged form.
 * Pure function — no S3 calls. Mirrors psifasbeetleutils.merge_structure_and_strings.
 */
export function mergeStructureAndStrings(
  structure: FormTemplate,
  strings: StringsFile,
): FormTemplate {
  const merged = deepCloneFormTemplate(structure);

  for (const question of merged.questions) {
    if (isPageBreak(question)) {
      continue;
    }
    const questionStrings = strings[question.guid];
    if (!questionStrings) {
      continue;
    }
    overlayQuestionStrings(question, questionStrings);
  }

  return merged;
}
